import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'

interface EnvVar {
  key: string
  label: string
  description: string
  required: boolean
  placeholder: string
  sensitive?: boolean
}

interface SwitchKeysDialogsProps {
  projectId: number
  integrationId: number
  environmentVariables: EnvVar[]
  showSwitchToOwnKeys: boolean
  showSwitchToCayuKeys: boolean
  onCloseSwitchToOwnKeys: () => void
  onCloseSwitchToCayuKeys: () => void
}

export function SwitchKeysDialogs({
  projectId,
  integrationId,
  environmentVariables,
  showSwitchToOwnKeys,
  showSwitchToCayuKeys,
  onCloseSwitchToOwnKeys,
  onCloseSwitchToCayuKeys,
}: SwitchKeysDialogsProps) {
  const { t } = useTranslation('conversations')
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [isSwitching, setIsSwitching] = useState(false)

  const handleSwitchToOwnKeys = async () => {
    // Validate required fields
    const requiredVars = environmentVariables.filter(ev => ev.required)
    const missingFields = requiredVars.filter(ev => !formValues[ev.key]?.trim())

    if (missingFields.length > 0) {
      toast.error(t('integrations.switchKeys.fillRequired', { fields: missingFields.map(f => f.label).join(', ') }))
      return
    }

    setIsSwitching(true)

    try {
      const response = await fetch(`/projects/${projectId}/integrations/${integrationId}/switch_to_own_keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ environment_variables: formValues })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.switchKeys.switchToOwnSuccess'))
        setFormValues({})
        onCloseSwitchToOwnKeys()
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.switchKeys.switchFailed')
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Switch to own keys error:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    } finally {
      setIsSwitching(false)
    }
  }

  const handleSwitchToCayuKeys = async () => {
    setIsSwitching(true)

    try {
      const response = await fetch(`/projects/${projectId}/integrations/${integrationId}/switch_to_cayu_keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({})
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || t('integrations.switchKeys.switchToCayuSuccess'))
        onCloseSwitchToCayuKeys()
        router.reload()
      } else {
        const errorMessage = data.errors ? data.errors.join(', ') : t('integrations.switchKeys.switchFailed')
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Switch to Cayu keys error:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    } finally {
      setIsSwitching(false)
    }
  }

  return (
    <>
      {/* Switch to Own Keys Dialog */}
      <AlertDialog open={showSwitchToOwnKeys} onOpenChange={onCloseSwitchToOwnKeys}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.switchKeys.ownKeysTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('integrations.switchKeys.ownKeysDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 my-4">
            {environmentVariables.filter(ev => ev.required).map((envVar) => (
              <div key={envVar.key}>
                <Label htmlFor={`switch-${envVar.key}`}>
                  {envVar.label} {envVar.required && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id={`switch-${envVar.key}`}
                  type={envVar.sensitive ? 'password' : 'text'}
                  placeholder={envVar.placeholder}
                  value={formValues[envVar.key] || ''}
                  onChange={(e) => setFormValues(prev => ({
                    ...prev,
                    [envVar.key]: e.target.value
                  }))}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">{envVar.description}</p>
              </div>
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSwitching}>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSwitchToOwnKeys} disabled={isSwitching}>
              {isSwitching ? t('integrations.switchKeys.switching') : t('integrations.switchKeys.switchToMyKeys')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Switch to Cayu Keys Dialog */}
      <AlertDialog open={showSwitchToCayuKeys} onOpenChange={onCloseSwitchToCayuKeys}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.switchKeys.cayuKeysTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('integrations.switchKeys.cayuKeysDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSwitching}>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSwitchToCayuKeys} disabled={isSwitching}>
              {isSwitching ? t('integrations.switchKeys.switching') : t('integrations.switchKeys.switchToCayuKeys')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
